import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Building2,
  MessageSquare,
  Plus,
  Wrench,
  X,
  Clock,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ticketService } from '../services/ticketService';
import { tenantService } from '../services/tenantService';
import { propertyService } from '../services/propertyService';
import { paymentService } from '../services/paymentService';
import Sidebar from '../components/Sidebar';
import FeedbackModal from '../components/FeedbackModal';

const TenantDashboard = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [tenantAssignments, setTenantAssignments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [payments, setPayments] = useState([]);

  const [paymentStats, setPaymentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  useEffect(() => {
    if (currentUser && userData) {
      console.log('Setting up tenant dashboard for:', currentUser.uid);
      
      const unsubscribeFunctions = [];

      try {
        // Subscribe to tickets
        const ticketUnsubscribe = ticketService.subscribeToTickets(
          currentUser.uid,
          'tenant',
          (tickets) => {
            console.log('Tenant tickets received:', tickets);
            setTickets(tickets || []);
          }
        );
        unsubscribeFunctions.push(ticketUnsubscribe);

        // Subscribe to tenant assignments
        const assignmentUnsubscribe = tenantService.subscribeToTenantAssignmentsByTenant(
          currentUser.uid,
          (assignments) => {
            console.log('Tenant assignments received:', assignments);
            setTenantAssignments(assignments || []);
          }
        );
        unsubscribeFunctions.push(assignmentUnsubscribe);

        // Subscribe to properties
        const propertyUnsubscribe = propertyService.subscribeToPropertiesByTenant(
          currentUser.uid,
          (properties) => {
            console.log('Tenant properties received:', properties);
            setProperties(properties || []);
          }
        );
        unsubscribeFunctions.push(propertyUnsubscribe);

        // Subscribe to payments
        const paymentUnsubscribe = paymentService.subscribeToPaymentsByTenant(
          currentUser.uid,
          (payments) => {
            console.log('Tenant payments received:', payments);
            setPayments(payments || []);
          }
        );
        unsubscribeFunctions.push(paymentUnsubscribe);

        // Load payment statistics
        const loadPaymentStats = async () => {
          try {
            const stats = await paymentService.getTenantPaymentStats(currentUser.uid);
            setPaymentStats(stats);
          } catch (error) {
            console.error('Error loading payment stats:', error);
          }
        };
        loadPaymentStats();

        setLoading(false);

        return () => {
          unsubscribeFunctions.forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
              unsubscribe();
            }
          });
        };
      } catch (error) {
        console.error('Error setting up tenant dashboard subscriptions:', error);
        setTickets([]);
        setTenantAssignments([]);
        setProperties([]);
        setPayments([]);
        setLoading(false);
      }
    } else {
      console.log('No currentUser or userData:', { currentUser, userData });
      setLoading(false);
    }
  }, [currentUser, userData]);

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    try {
      // Determine active assignment to populate owner and property
      const activeAssignment = tenantAssignments.find(a => a.status === 'active') || tenantAssignments[0];
      if (!activeAssignment) {
        alert('No active property assignment found. Please contact the owner.');
        return;
      }

      const property = properties.find(p => p.id === activeAssignment.propertyId);
      await ticketService.createTicket({
        title: newTicket.title,
        description: newTicket.description,
        status: 'open',
        priority: newTicket.priority,
        category: newTicket.category,
        tenantId: currentUser.uid,
        tenantName: userData?.displayName || currentUser.email,
        ownerId: activeAssignment.ownerId,
        propertyId: activeAssignment.propertyId,
        propertyName: property?.name || activeAssignment.propertyName || 'Unknown Property'
      });
      setShowNewTicket(false);
      setNewTicket({ title: '', description: '', priority: 'medium', category: 'general' });
      alert('Ticket submitted successfully!');
    } catch (error) {
      console.error('Error submitting ticket:', error);
      alert('Error submitting ticket: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'in-progress':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'completed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'closed':
        return 'bg-slate-50 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-slate-600 mx-auto" />
          <p className="mt-4 text-slate-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <Sidebar />

        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10 w-full">
            <div className="flex items-center justify-between px-8 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <LayoutDashboard className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-slate-800">{t('dashboard.tenant.title')}</h1>
                  <p className="text-slate-500 text-sm">{t('dashboard.tenant.welcome')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFeedbackModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
                  title={t('feedback.submit')}
                >
                  <MessageSquare className="w-4 h-4" strokeWidth={1.75} />
                  <span className="hidden sm:inline">{t('feedback.submit')}</span>
                </button>
                <div className="text-sm text-slate-500 pl-3 border-l border-slate-200">
                  {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                </div>
              </div>
            </div>
          </header>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white shadow-lg border border-slate-700">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center text-lg font-semibold border border-white/20">
                    {userData?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="text-slate-300 text-sm font-medium">Hello, {userData?.displayName?.split(' ')[0] || 'Tenant'}!</p>
                    <p className="text-slate-100 font-semibold">Welcome back</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">{tickets.length}</p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-0.5">My Tickets</p>
                  </div>
                  <div className="w-11 h-11 bg-slate-100 rounded-xl flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">
                      {tickets.filter(t => t.status === 'open').length}
                    </p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-0.5">Open Requests</p>
                  </div>
                  <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">{properties.length}</p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-0.5">My Property</p>
                  </div>
                  <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-emerald-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-base font-semibold text-slate-800 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => setShowNewTicket(true)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 text-white text-sm font-medium rounded-xl hover:bg-slate-700 transition-colors shadow-sm"
                  >
                    <Plus className="w-5 h-5" strokeWidth={1.75} />
                    Submit New Request
                  </button>
                  <Link
                    to="/tickets"
                    className="flex items-center justify-center gap-2 px-4 py-3 border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    <ClipboardList className="w-5 h-5" strokeWidth={1.75} />
                    View All Requests
                    <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
                  </Link>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  My Rental Property
                </h3>
                {properties.length > 0 ? (
                  <div className="space-y-4">
                    {properties.map((property) => {
                      const assignment = tenantAssignments.find(a => a.propertyId === property.id);
                      return (
                        <div key={property.id} className="border border-slate-200 rounded-xl p-4 bg-slate-50/80 hover:border-slate-300 transition-colors">
                          <h4 className="font-semibold text-slate-800">{property.name}</h4>
                          <p className="text-sm text-slate-500 mt-0.5">{property.address}</p>
                          <div className="mt-3 flex items-center gap-3 flex-wrap">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Active Lease
                            </span>
                            {assignment?.leaseEndDate && (
                              <span className="text-xs text-slate-500">
                                Lease ends: {assignment.leaseEndDate?.toDate?.().toLocaleDateString() || 'N/A'}
                              </span>
                            )}
                          </div>
                          {assignment && (
                            <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600 space-y-0.5">
                              <p>Monthly Rent: <span className="font-semibold text-slate-800">${assignment.monthlyRent}</span></p>
                              <p>Lease Start: {assignment.leaseStartDate?.toDate?.().toLocaleDateString()}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Building2 className="w-7 h-7 text-slate-400" strokeWidth={1.75} />
                    </div>
                    <h3 className="text-base font-medium text-slate-800 mb-2">No rental property</h3>
                    <p className="text-sm text-slate-500">You haven&apos;t been assigned to any properties yet.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  Recent Requests
                </h3>
                <Link
                  to="/tickets"
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  View All
                  <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
              </div>

              {tickets.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-7 h-7 text-amber-500" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-base font-medium text-slate-800 mb-2">No requests yet</h3>
                  <p className="text-sm text-slate-500 mb-4">Submit your first maintenance request to get started.</p>
                  <button
                    onClick={() => setShowNewTicket(true)}
                    className="px-5 py-2.5 bg-slate-800 text-white text-sm font-medium rounded-xl hover:bg-slate-700 transition-colors"
                  >
                    Submit Your First Request
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.slice(0, 5).map((ticket) => (
                    <div key={ticket.id} className="flex items-center gap-4 p-4 bg-slate-50/80 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors">
                      <div className="w-11 h-11 bg-slate-100 rounded-xl flex items-center justify-center shrink-0">
                        <Wrench className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="text-sm font-medium text-slate-800 hover:text-slate-600 transition-colors block truncate"
                        >
                          {ticket.title || 'Untitled Request'}
                        </Link>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{ticket.description?.substring(0, 50)}{ticket.description?.length > 50 ? '…' : ''}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-md overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
          <div className="relative top-12 w-full max-w-md bg-white border border-slate-200 shadow-xl rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/50">
              <h3 className="text-lg font-semibold text-slate-800">Submit New Request</h3>
              <button
                onClick={() => setShowNewTicket(false)}
                className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>
            <form onSubmit={handleSubmitTicket} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Request Title</label>
                <input
                  type="text"
                  required
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all text-sm"
                  placeholder="Brief description of the issue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
                <textarea
                  required
                  rows={3}
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all text-sm resize-none"
                  placeholder="Detailed description of the issue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Priority</label>
                <select
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Category</label>
                <select
                  value={newTicket.category}
                  onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all text-sm"
                >
                  <option value="general">General</option>
                  <option value="plumbing">Plumbing</option>
                  <option value="electrical">Electrical</option>
                  <option value="hvac">HVAC</option>
                  <option value="appliance">Appliance</option>
                  <option value="structural">Structural</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewTicket(false)}
                  className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </div>
  );
};

export default TenantDashboard; 